/**
 * ItemLinearSyncPanel — link this item to a Linear issue, and keep what the
 * issue says on the item.
 *
 * Pick a project (remembered for next time), find the issue by a few words of
 * its title or its key (PRI-12), link it. Linking brings the issue's fields
 * over through the workspace's Linear field mappings — by default the
 * description and the priority, which lands in the custom field "Priority".
 * Sync now re-reads the issue; the item page also re-reads a link that has
 * gone stale when it opens. Shown inside ItemIntegrationsModal, beside Jira.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Link2, Link2Off, Loader2, RefreshCw, Search } from 'lucide-react';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useItemExternalLinks, externalLinkDisplayKey, type ItemExternalLink } from '@/hooks/prioritization/useItemExternalLinks';
import {
  linearProjects,
  linearSearchIssues,
  linearStatus,
  linearTeams,
  type LinearIssueSummary,
  type LinearProjectSummary,
  type LinearTeamSummary,
} from '@/lib/semanticConnectors/linearSemanticApi';
import {
  describeLinearSync,
  linkItemToLinearIssue,
  syncLinkedLinearIssue,
  unlinkItemFromLinear,
  type LinearSyncResult,
} from '@/lib/linearItemSync';

const CONNECTORS_PATH = '/prioritization/settings/connectors';
const FIELD_MAPPINGS_PATH = '/prioritization/settings/field-mappings';
const CUSTOM_FIELDS_PATH = '/prioritization/settings/custom-fields';

/**
 * Where to look for the issue: the whole Linear workspace, one team, or one
 * project — encoded as '' | 'team:<id>' | 'project:<id>'. The choice made
 * last time is remembered per workspace (a convenience, not a record).
 */
function rememberedScopeKey(workspaceId: string): string {
  return `pp-linear-scope:${workspaceId}`;
}
function readRememberedScope(workspaceId: string): string {
  try {
    return localStorage.getItem(rememberedScopeKey(workspaceId)) ?? '';
  } catch {
    return '';
  }
}
function rememberScope(workspaceId: string, scope: string): void {
  try {
    localStorage.setItem(rememberedScopeKey(workspaceId), scope);
  } catch {
    /* a convenience only */
  }
}
function scopeFilter(scope: string): { teamId?: string; projectId?: string } {
  if (scope.startsWith('team:')) return { teamId: scope.slice('team:'.length) };
  if (scope.startsWith('project:')) return { projectId: scope.slice('project:'.length) };
  return {};
}

function whenSynced(iso: string | null): string {
  if (!iso) return 'not synced yet';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'synced just now';
  if (ms < 3_600_000) return `synced ${Math.round(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `synced ${Math.round(ms / 3_600_000)} h ago`;
  return `synced ${new Date(iso).toLocaleDateString()}`;
}

function LinearMark({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" className={className} fill="currentColor" aria-hidden="true">
      <path d="M1.2 61.6c-.4-1.6 1.6-2.7 2.8-1.5l35.9 35.9c1.2 1.2.1 3.2-1.5 2.8C19.6 94 6 80.4 1.2 61.6zM0 44.4c0 .5.2 1 .6 1.4L54.2 99.4c.4.4.9.6 1.4.6 1.6 0 3.1-.1 4.6-.3 1.3-.2 1.9-1.8.9-2.8L2.9 38.9c-1-1-2.6-.4-2.8.9C0 41.3 0 42.8 0 44.4zm3.5-15.9c-.3.7-.1 1.5.4 2L69.5 96.1c.5.5 1.3.7 2 .4 1.2-.5 2.4-1.1 3.6-1.7.9-.5 1.1-1.7.4-2.4L7.6 24.8c-.7-.7-1.9-.5-2.4.4-.6 1.2-1.2 2.4-1.7 3.3zm9.1-12.3c-.6-.6-.6-1.6 0-2.2C21.7 4.6 35.1 0 50 0c27.6 0 50 22.4 50 50 0 14.9-4.6 28.3-14 37.4-.6.6-1.6.6-2.2 0L12.6 16.2z" />
    </svg>
  );
}

export function ItemLinearSyncPanel({ itemId, itemTitle }: { itemId: string; itemTitle?: string }) {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const mayEdit = useUserStore((s) => s.can('items.edit'));
  const mayAdminister = useUserStore((s) => s.can('admin.settings'));

  const allLinks = useItemExternalLinks(workspaceId, itemId);
  const links: ItemExternalLink[] = (allLinks ?? []).filter((l) => l.connectorType === 'linear');

  const [connected, setConnected] = useState<boolean | null>(null);
  const [teams, setTeams] = useState<LinearTeamSummary[]>([]);
  const [projects, setProjects] = useState<LinearProjectSummary[]>([]);
  const [scope, setScope] = useState<string>(() => (workspaceId ? readRememberedScope(workspaceId) : ''));
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LinearIssueSummary[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState<string | null>(null); // issue id being linked / synced / unlinked
  const [message, setMessage] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string; detail?: string[] } | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Connection, teams and projects, once.
  useEffect(() => {
    if (!workspaceId) {
      setConnected(false);
      return;
    }
    let live = true;
    linearStatus(workspaceId)
      .then(async (s) => {
        if (!live) return;
        setConnected(s.connected);
        if (!s.connected) return;
        const [teamList, projectList] = await Promise.all([
          linearTeams(workspaceId).catch(() => [] as LinearTeamSummary[]),
          linearProjects(workspaceId).catch(() => [] as LinearProjectSummary[]),
        ]);
        if (!live) return;
        setTeams(teamList);
        setProjects(projectList);
        // A remembered team or project that is gone falls back to the whole workspace.
        setScope((current) => {
          const f = scopeFilter(current);
          if (f.teamId && !teamList.some((t) => t.id === f.teamId)) return '';
          if (f.projectId && !projectList.some((p) => p.id === f.projectId)) return '';
          return current;
        });
      })
      .catch(() => live && setConnected(false));
    return () => {
      live = false;
    };
  }, [workspaceId]);

  // Search as the person types (and when the scope changes).
  useEffect(() => {
    if (!workspaceId || !connected || !mayEdit) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearching(true);
      linearSearchIssues(workspaceId, { ...scopeFilter(scope), query: query.trim() || undefined, first: 25 })
        .then((issues) => setResults(issues))
        .catch((err) => setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Linear could not be searched' }))
        .finally(() => setSearching(false));
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [workspaceId, connected, mayEdit, scope, query]);

  const report = useCallback((result: LinearSyncResult) => {
    const detail: string[] = [];
    if (result.fieldCreated) detail.push('Created the custom field “Priority” for this workspace.');
    if (result.setCreated) detail.push('Saved the Linear default field mappings for this workspace.');
    for (const k of result.kept) if (k.reason && !k.reason.startsWith('already')) detail.push(`${k.label}: kept — ${k.reason}.`);
    for (const s of result.skipped) detail.push(`${s.label}: not written — ${s.reason}.`);
    for (const e of result.errors.slice(1)) detail.push(e);
    setMessage({ tone: result.ok ? 'ok' : result.wrote.length ? 'warn' : 'error', text: describeLinearSync(result), detail });
  }, []);

  const link = async (issue: LinearIssueSummary) => {
    if (!workspaceId) return;
    setBusy(issue.id);
    setMessage(null);
    try {
      report(await linkItemToLinearIssue({ workspaceId, itemId, issue, userId }));
      setQuery('');
    } finally {
      setBusy(null);
    }
  };

  const syncNow = async (l: ItemExternalLink) => {
    if (!workspaceId) return;
    setBusy(l.externalId);
    setMessage(null);
    try {
      report(await syncLinkedLinearIssue({ workspaceId, itemId, issueId: l.externalId, userId, reason: 'now' }));
    } finally {
      setBusy(null);
    }
  };

  const unlink = async (l: ItemExternalLink) => {
    if (!workspaceId) return;
    setBusy(l.externalId);
    setMessage(null);
    try {
      const { error } = await unlinkItemFromLinear({ workspaceId, itemId, externalId: l.externalId });
      setMessage(
        error
          ? { tone: 'error', text: `The link to ${externalLinkDisplayKey(l)} was not removed: ${error}` }
          : { tone: 'ok', text: `Unlinked from ${externalLinkDisplayKey(l)}. What was brought over stays on the item.` },
      );
    } finally {
      setBusy(null);
    }
  };

  const linkedIds = new Set(links.map((l) => l.externalId));

  return (
    <section className="border border-[var(--rule)] p-4" data-testid="item-linear-sync-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <LinearMark className="h-4 w-4 text-[var(--text-sec)]" />
          <h4 className="text-sm font-semibold text-[var(--text-pri)]">Linear</h4>
        </div>
        {connected === null ? (
          <span className="text-xs text-[var(--text-ter)]">Checking…</span>
        ) : connected ? (
          <span className="text-xs text-[var(--text-ter)]">Connected</span>
        ) : (
          <span className="text-xs text-[var(--text-ter)]">Not connected</span>
        )}
      </div>

      {/* Linked issues */}
      <div className="mt-3 space-y-2" data-testid="item-linear-links">
        {allLinks === null ? (
          <p className="text-sm text-[var(--text-ter)]">Reading links…</p>
        ) : links.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)]">Not linked to a Linear issue.</p>
        ) : (
          links.map((l) => (
            <div key={l.externalId} className="flex flex-wrap items-center justify-between gap-2 border border-[var(--rule)] px-3 py-2">
              <p className="text-sm text-[var(--text-sec)]">
                {l.externalUrl ? (
                  <a href={l.externalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono font-medium text-[var(--text-pri)] underline">
                    {externalLinkDisplayKey(l)}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="font-mono font-medium text-[var(--text-pri)]">{externalLinkDisplayKey(l)}</span>
                )}
                <span className="text-[var(--text-ter)]"> · {whenSynced(l.lastSyncedAt)}</span>
              </p>
              {mayEdit && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => void syncNow(l)}
                    disabled={busy !== null || !connected}
                    className="inline-flex items-center gap-1 border border-[var(--rule)] px-2 py-1 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50"
                    data-testid="item-linear-sync-now"
                  >
                    {busy === l.externalId ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    Sync now
                  </button>
                  <button
                    type="button"
                    onClick={() => void unlink(l)}
                    disabled={busy !== null}
                    className="inline-flex items-center gap-1 border border-[var(--rule)] px-2 py-1 text-xs font-medium text-[var(--text-ter)] hover:bg-[var(--surface)] disabled:opacity-50"
                    title="Remove the link; what was brought over stays on the item"
                  >
                    <Link2Off className="h-3 w-3" />
                    Unlink
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {message && (
        <div
          className={`mt-3 border px-3 py-2 text-sm ${
            message.tone === 'error'
              ? 'border-[var(--red)] text-[var(--red)]'
              : message.tone === 'warn'
                ? 'border-[var(--mustard)] text-[var(--text-sec)]'
                : 'border-[var(--rule)] text-[var(--text-sec)]'
          }`}
          data-testid="item-linear-message"
        >
          <p>{message.text}</p>
          {message.detail && message.detail.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-xs text-[var(--text-ter)]">
              {message.detail.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Link to an issue */}
      {connected === false ? (
        <p className="mt-3 text-sm text-[var(--text-ter)]">
          Linear is not connected for this workspace.
          {mayAdminister ? (
            <>
              {' '}
              <Link to={CONNECTORS_PATH} className="underline hover:text-[var(--text-pri)]">
                Connect it in Settings → Connectors
              </Link>
              .
            </>
          ) : (
            ' A workspace admin connects it in Settings → Connectors.'
          )}
        </p>
      ) : connected && !mayEdit ? (
        <p className="mt-3 text-sm text-[var(--text-ter)]">Linking an issue needs the permission to edit items.</p>
      ) : connected ? (
        <div className="mt-4 space-y-2" data-testid="item-linear-link-form">
          <p className="text-xs font-medium text-[var(--text-sec)]">
            Link {itemTitle ? <>“{itemTitle}”</> : 'this item'} to an issue
          </p>
          <div className="flex flex-wrap gap-2">
            <select
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                if (workspaceId) rememberScope(workspaceId, e.target.value);
              }}
              className="min-w-[12rem] border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm"
              aria-label="Where to look in Linear"
              data-testid="item-linear-scope"
            >
              <option value="">Anywhere in Linear</option>
              {teams.length > 0 && (
                <optgroup label="Teams">
                  {teams.map((t) => (
                    <option key={t.id} value={`team:${t.id}`}>
                      {t.name}
                      {t.key ? ` (${t.key})` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              {projects.length > 0 && (
                <optgroup label="Projects">
                  {projects.map((p) => (
                    <option key={p.id} value={`project:${p.id}`}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <div className="relative flex-1 min-w-[12rem]">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-ter)]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Title words or a key like PRI-12"
                className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] py-1.5 pl-7 pr-2 text-sm"
                aria-label="Find a Linear issue"
                data-testid="item-linear-search"
              />
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto border border-[var(--rule)]" data-testid="item-linear-results">
            {searching && results === null ? (
              <p className="px-3 py-2 text-sm text-[var(--text-ter)]">Reading issues…</p>
            ) : results && results.length === 0 ? (
              <p className="px-3 py-2 text-sm text-[var(--text-ter)]">No open issues match.</p>
            ) : (
              (results ?? []).map((issue) => {
                const already = linkedIds.has(issue.id);
                return (
                  <div key={issue.id} className="flex items-center justify-between gap-3 border-b border-[var(--rule)] px-3 py-2 last:border-b-0">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-[var(--text-pri)]">
                        <span className="font-mono text-xs text-[var(--text-sec)]">{issue.identifier}</span> {issue.title}
                      </p>
                      <p className="text-xs text-[var(--text-ter)]">
                        {issue.priorityLabel}
                        {issue.state?.name ? ` · ${issue.state.name}` : ''}
                        {issue.project?.name ? ` · ${issue.project.name}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void link(issue)}
                      disabled={busy !== null || already}
                      className="inline-flex flex-shrink-0 items-center gap-1 border border-[var(--rule)] px-2 py-1 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50"
                      data-testid="item-linear-link-button"
                    >
                      {busy === issue.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                      {already ? 'Linked' : 'Link'}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <p className="text-xs text-[var(--text-ter)]">
            Linking brings over what the Linear field mappings name — the description, and the priority into the field “Priority”, unless the workspace maps otherwise.
            {mayAdminister && (
              <>
                {' '}
                <Link to={FIELD_MAPPINGS_PATH} className="underline hover:text-[var(--text-pri)]">
                  Field Mappings
                </Link>
                {' · '}
                <Link to={CUSTOM_FIELDS_PATH} className="underline hover:text-[var(--text-pri)]">
                  Custom Fields
                </Link>
              </>
            )}
          </p>
        </div>
      ) : null}
    </section>
  );
}

export default ItemLinearSyncPanel;
